/**
 * Florida sales-tax jurisdiction configuration — Phase 10.
 *
 * Florida levies 6% state sales tax + a discretionary county surtax.
 * Home-improvement contractors on real-property improvements are
 * generally end consumers of materials (tax paid to supplier; not
 * collected from customer). Tangible-personal-property sales DO require
 * collection. This config supports both via per-jurisdiction tracking.
 *
 * Rates verified June 2026 — admin can edit on the Sales Tax Center.
 */

export interface TaxJurisdiction {
  id: string;          // canonical lookup key, e.g. "FL-HILLSBOROUGH"
  state: "FL";
  countyName: string;  // "Hillsborough" or "State" for the FL portion
  stateRate: number;   // 0.06 always for FL
  countyRate: number;  // 0..0.015 typical
  /** Suggested filing cadence: monthly, quarterly, annual. */
  cadence: "monthly" | "quarterly" | "annual";
  active: boolean;
}

const FL = (county: string, countyRate: number, cadence: TaxJurisdiction["cadence"] = "monthly"): TaxJurisdiction => ({
  id: `FL-${county.toUpperCase().replace(/\s+/g, "_")}`,
  state: "FL",
  countyName: county,
  stateRate: 0.06,
  countyRate,
  cadence,
  active: true,
});

/** Tampa–Fort Myers–Lakeland focus per project geographic memory. */
export const FL_JURISDICTIONS: TaxJurisdiction[] = [
  FL("Hillsborough", 0.015),
  FL("Pinellas", 0.01),
  FL("Pasco", 0.01),
  FL("Polk", 0.01),
  FL("Hernando", 0.005),
  FL("Manatee", 0.01),
  FL("Sarasota", 0.01),
  FL("Lee", 0.005),
  FL("Charlotte", 0.01),
  FL("Collier", 0),
];

export function totalRate(j: TaxJurisdiction): number {
  return j.stateRate + j.countyRate;
}

/** Tag used on JE line memos to attribute tax to a jurisdiction. */
export const TAX_MEMO_PREFIX = "tax:";

export function taxMemo(jurisdictionId: string, label?: string): string {
  return `${TAX_MEMO_PREFIX}${jurisdictionId}${label ? ` · ${label}` : ""}`;
}

export function parseJurisdictionFromMemo(memo: string | undefined): string | null {
  if (!memo) return null;
  const m = memo.match(/tax:([A-Z0-9_-]+)/);
  return m?.[1] ?? null;
}
